import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import EditIcon from '../../atoms/edit/edit';
import { PermissionList } from 'types/permissions';
import { Sphere } from 'types/global';
import './EditPill.scss';

interface EditPillMoleculeProps {
  onEditChange: (edit: string) => void;
  isOpen: boolean;
  onClose: () => void;
  showDeactivate: boolean;
  showConversation?: boolean; // Show conversation option if chat is enabled
  /** Show "Deploy approach" option (value 5) — requires edit permission + deploy gate on. */
  showDeployApproach?: boolean;
  parentNode?: Sphere;
  sphere?: Sphere;
  fetchPermissionList: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
}

const EditPillMolecule: React.FC<EditPillMoleculeProps> = ({
  isOpen,
  onClose,
  onEditChange,
  showDeactivate,
  showConversation = false,
  showDeployApproach = false,
  parentNode,
  sphere,
  fetchPermissionList,
}) => {
  const pillRef = useRef<HTMLDivElement>(null);
  const [hasViewPermission, setHasViewPermission] = useState<boolean | null>(
    null
  );
  const [hasEditPermission, setHasEditPermission] = useState<boolean | null>(
    null
  );
  const [hasDeletePermission, setHasDeletePermission] = useState<
    boolean | null
  >(null);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animationClass, setAnimationClass] = useState('');
  const [positionAbove, setPositionAbove] = useState(false);

  // Check permissions before rendering the pill
  const checkPermission = useCallback(async () => {
    // 1) Decide which ID to fetch permissions for
    const resourceId = sphere?.id;
    if (!resourceId) {
      // No valid ID to check – bail out
      setHasViewPermission(false);
      setHasEditPermission(false);
      setHasDeletePermission(false);
      onClose();
      return;
    }

    // 2) Decide which label your backend needs
    const resourceLabel = sphere?.label || 'sphere';

    try {
      // Reset permission states to show a loading (indeterminate) state
      setHasViewPermission(null);
      setHasEditPermission(null);
      setHasDeletePermission(null);

      // 3) Fetch the permissions (now including "view")
      const permissionList = await fetchPermissionList(
        resourceId,
        resourceLabel,
        ['view', 'edit', 'delete']
      );

      // Extract permissions; if a permission is missing, default to false
      const canView = permissionList?.permissions?.view ?? false;
      const canEdit = permissionList?.permissions?.edit ?? false;
      const canDelete = permissionList?.permissions?.delete ?? false;

      setHasViewPermission(canView);
      setHasEditPermission(canEdit);
      setHasDeletePermission(canDelete);

      // If the user lacks all permissions, close the pill
      if (!canView && !canEdit && !canDelete) {
        onClose();
      }
    } catch (error) {
      console.error('EditPill: Permission check failed:', error);
      onClose();
    }
  }, [parentNode, sphere, fetchPermissionList, onClose]);

  // Effect 1: Run permission check only when isOpen becomes true.
  useEffect(() => {
    if (isOpen) {
      checkPermission();
    }
  }, [isOpen, checkPermission]);

  // Effect 2: Handle animation and visibility.
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Delay to allow rendering before adding the "visible" class.
      const timer = setTimeout(() => {
        setAnimationClass('visible');
      }, 10);
      return () => clearTimeout(timer);
    } else if (shouldRender) {
      setAnimationClass('hidden'); // Trigger hide transition
      const timer = setTimeout(() => {
        setShouldRender(false);
        setAnimationClass('');
      }, 300); // Match your CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Build the list of available actions - MUST be before positioning effect
  const choices: { label: string; value: number }[] = useMemo(() => {
    const items: { label: string; value: number }[] = [];

    // If the user can view, add the View pill (value 0) as the first option
    if (hasViewPermission) {
      items.unshift({ label: 'View', value: 0 });
    }

    if (hasEditPermission) {
      // "Add" (value = 3) and "Edit" (value = 2)
      items.push({ label: 'Add', value: 3 }, { label: 'Edit', value: 2 });
    }

    if (showDeactivate && hasDeletePermission) {
      // "Deactivate" (value = 1)
      items.push({ label: 'Deactivate', value: 1 });
    }

    // Add Conversation option if chat is enabled (value 4)
    if (showConversation) {
      items.push({ label: 'Conversation', value: 4 });
    }

    // Deploy approach (value 5) — edit permission required; only when gate is on
    if (showDeployApproach && hasEditPermission) {
      items.push({ label: 'Deploy approach', value: 5 });
    }

    return items;
  }, [hasViewPermission, hasEditPermission, hasDeletePermission, showDeactivate, showConversation, showDeployApproach]);

  // Effect 3: Check positioning to avoid footer overlap
  useEffect(() => {
    if (isOpen && pillRef.current) {
      const checkPosition = () => {
        const pill = pillRef.current;
        if (!pill) return;

        // Get the parent element (trigger) position
        const parent = pill.parentElement;
        if (!parent) return;

        const parentRect = parent.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // On mobile, menu is vertical (items stacked), so calculate actual menu height
        const isMobile = window.innerWidth <= 500;

        // For mobile, use more conservative threshold
        // Mobile menu needs significantly more space due to vertical layout
        const itemCount = choices.length;
        const estimatedMobileHeight = itemCount * 70; // Increased estimate for mobile items with padding
        const footerHeight = isMobile ? 150 : 100; // Larger buffer for mobile

        const menuHeight = isMobile
          ? Math.max(pill.offsetHeight, estimatedMobileHeight)
          : 60; // Desktop horizontal layout

        // Calculate space below the parent trigger element
        const spaceBelow = viewportHeight - parentRect.bottom - footerHeight;

        // On mobile, be more aggressive - if we're in bottom half of screen, position above
        if (isMobile) {
          const isInBottomHalf = parentRect.bottom > viewportHeight / 2;
          setPositionAbove(isInBottomHalf || spaceBelow < menuHeight);
        } else {
          setPositionAbove(spaceBelow < menuHeight);
        }
      };

      // Check position multiple times to ensure DOM is fully rendered
      const timer1 = setTimeout(checkPosition, 10);
      const timer2 = setTimeout(checkPosition, 100);
      const timer3 = setTimeout(checkPosition, 250);

      // Recheck on scroll or resize
      window.addEventListener('scroll', checkPosition, true);
      window.addEventListener('resize', checkPosition);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        window.removeEventListener('scroll', checkPosition, true);
        window.removeEventListener('resize', checkPosition);
      };
    }
  }, [isOpen, choices.length]);

  useEffect(() => {
    const handleDocClick = (ev: MouseEvent) => {
      if (
        isOpen &&
        pillRef.current &&
        !pillRef.current.contains(ev.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleDocClick, true);
    return () => {
      document.removeEventListener('mousedown', handleDocClick, true);
    };
  }, [isOpen, onClose]);

  // If no choices are available, render nothing.
  if (!shouldRender || choices.length === 0) {
    return null;
  }

  return (
    <div
      className={`edit-pill ${animationClass} ${positionAbove ? 'edit-pill--above' : ''}`}
      ref={pillRef}
    >
      {choices.map((choice) => (
        <div
          key={choice.value}
          className="edit-item"
          onClick={(e) => {
            e.stopPropagation();
            onEditChange(choice.value.toString());
          }}
        >
          <EditIcon editValue={choice.value} /> {choice.label}
        </div>
      ))}
    </div>
  );
};

export default EditPillMolecule;
